import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/utils/store';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import PhotoPicker from '@/components/ui/PhotoPicker';

const MEMBER_COLORS = ['#3b82f6', '#ec4899', '#f59e0b', '#10b981', '#8b5cf6', '#f97316'];
const RELATIONSHIPS = ['yo', 'pareja', 'hijo', 'padre', 'madre', 'hermano', 'mascota', 'otro'];
const EMOJI_OPTIONS = ['😊', '😎', '🥰', '👨', '👩', '👦', '👧', '👶', '👧🏻', '👶🏻', '👩🏻', '🧔', '👵', '👴', '🐶', '🐱', '🦊', '🐻', '🐼'];

function getInitials(name) {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export default function AddMemberModal({ open, onClose, member }) {
  const { t } = useTranslation();
  const addMember = useStore((s) => s.addMember);
  const updateMember = useStore((s) => s.updateMember);
  const isEdit = !!member;

  const [name, setName] = useState('');
  const [relationship, setRelationship] = useState('');
  const [color, setColor] = useState(MEMBER_COLORS[0]);
  const [photo, setPhoto] = useState(null);
  const [emoji, setEmoji] = useState(null);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!name.trim()) errs.name = t('family.form.nameRequired');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    const data = {
      name: name.trim(),
      relationship: relationship || 'otro',
      initials: getInitials(name.trim()),
      color,
      photo: photo || undefined,
      emoji: emoji || undefined,
    };
    if (isEdit) {
      updateMember(member.id, data);
    } else {
      addMember(data);
    }
    handleClose();
  };

  const handleClose = () => {
    setName('');
    setRelationship('');
    setColor(MEMBER_COLORS[0]);
    setPhoto(null);
    setEmoji(null);
    setErrors({});
    onClose();
  };

  // Populate form when editing
  useEffect(() => {
    if (member) {
      setName(member.name);
      setRelationship(member.relationship);
      setColor(member.color);
      setPhoto(member.photo || null);
      setEmoji(member.emoji || null);
    }
  }, [member]);

  return (
    <Modal open={open} onClose={handleClose} title={isEdit ? t('common.edit') : t('family.addMember')} footer={
      <Button className="w-full" onClick={handleSave}>
        {t('common.save')}
      </Button>
    }>
      <div className="space-y-4">
        {/* Photo / Emoji avatar */}
        <div className="flex justify-center">
          {emoji && !photo ? (
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center text-3xl"
              style={{ backgroundColor: color + '20' }}
            >
              {emoji}
            </div>
          ) : (
            <PhotoPicker
              photo={photo}
              initials={name ? getInitials(name) : '?'}
              color={color}
              size={64}
              onPhotoChange={(p) => { setPhoto(p); setEmoji(null); }}
            />
          )}
        </div>
        {(photo || emoji) && (
          <button
            onClick={() => { setPhoto(null); setEmoji(null); }}
            className="block mx-auto text-xs text-danger-500 hover:text-danger-600 transition-colors"
          >
            {t('settings.removePhoto')}
          </button>
        )}

        {/* Emoji picker */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('family.form.emoji')}
          </label>
          <div className="flex gap-2 flex-wrap">
            {EMOJI_OPTIONS.map((em) => (
              <button
                key={em}
                onClick={() => { setEmoji(em); setPhoto(null); }}
                className={`w-10 h-10 rounded-xl text-xl flex items-center justify-center border-2 transition-all ${
                  emoji === em
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 scale-110'
                    : 'border-gray-200 dark:border-gray-700 hover:border-brand-400'
                }`}
              >
                {em}
              </button>
            ))}
          </div>
        </div>

        {/* Name */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('family.form.name')}
          </label>
          <input
            type="text"
            className={`input ${errors.name ? 'border-danger-500' : ''}`}
            placeholder={t('family.form.namePlaceholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {errors.name && <p className="text-xs text-danger-500 mt-1">{errors.name}</p>}
        </div>

        {/* Relationship */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('family.form.relationship')}
          </label>
          <div className="grid grid-cols-4 gap-2">
            {RELATIONSHIPS.map((r) => (
              <button
                key={r}
                onClick={() => setRelationship(r)}
                className={`py-2 px-2 text-xs font-medium rounded-xl border transition-all ${
                  relationship === r
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-brand-400'
                }`}
              >
                {t(`family.relationships.${r}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Color */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('family.form.color')}
          </label>
          <div className="flex gap-3">
            {MEMBER_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                className={`w-10 h-10 rounded-xl transition-all ${
                  color === c ? 'ring-2 ring-offset-2 ring-brand-500 dark:ring-offset-gray-900' : ''
                }`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>

      </div>
    </Modal>
  );
}
